/* eslint-disable no-param-reassign */
import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import {
  MineModelDto,
  ProjectDto,
  useDxfImportMutation,
  UnitSystem
} from 'providers/api';
import { UseQueryResult } from 'react-query';
import { trackEvent } from 'providers/analytics';
import processDxfToJson from '../../DxfImporter/DxfParser';
import {
  buildImportPayload,
  checkDistanceToCenterForPCNodes,
  idMapFromRepoints,
  ImportGraph,
  previewNodesToModel,
  remapEdges
} from './sharedImportUtils';
import { Model } from '../../hooks/useModelState';
import { PCNode } from '../../MineModel/Nodes';
import { validateNodes } from '../utils/validation';
import { PCPipe } from '../../MineModel/Pipes';
import { SpoolEdge } from '../utils/nodeCreation';
import PCSpool from '../../MineModel/Spools/PCSpool';

interface UseDxfImportProps {
  project: ProjectDto;
  mineModel: MineModelDto;
  model: Model;
  mineModelQuery: UseQueryResult<MineModelDto | undefined, unknown>;
  isAdminOrPc?: boolean;
}

const useDxfImport = ({
  model,
  project,
  mineModelQuery,
  isAdminOrPc,
}: UseDxfImportProps) => {
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewNodes, setPreviewNodes] = useState<PCNode[]>([]);
  const [previewEdges, setPreviewEdges] = useState<SpoolEdge[]>([]);
  const [previewSpools, setPreviewSpools] = useState<PCSpool[]>([]);
  const mutation = useDxfImportMutation();

  // The import form has already closed by the time a file is read, so its error line is not on screen.
  const showError = (message: string) => {
    setError(message);
    enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
  };

  const removePreviewNodes = () => {
    if (!model || previewNodes.length === 0) return;
    model.removeNodes(previewNodes);
    if (previewSpools.length > 0) {
      model.removeSpools(previewSpools);
    }
    setPreviewNodes([]);
    setPreviewEdges([]);
    setPreviewSpools([]);
  };

  const previewDxfNodes = (graph: ImportGraph) => {
    if (!model) {
      setError('Model state not initialized');
      return;
    }
    let addedSpools: PCSpool[] = [];
    try {
      addedSpools = previewNodesToModel(graph, model);

      setPreviewNodes(graph.nodes);
      setPreviewEdges(graph.edges);
      setPreviewSpools(addedSpools);
    } catch {
      setError('Error previewing nodes');
      if (graph.nodes.length > 0) {
        model.removeNodes(graph.nodes);
      }
      if (addedSpools.length > 0) {
        model.removeSpools(addedSpools);
      }
    }
  };

  // Nodes that duplicate an existing coordinate are dropped and nodes whose id is taken are renamed, so
  // the edges have to follow: an edge left pointing at a dropped or renamed id is pruned by the server
  // and the imported pipe silently is not there.
  const applyValidation = (graph: ImportGraph): ImportGraph => {
    const validationResult = validateNodes(graph.nodes, Object.values(model.nodes), 1.0);
    if (validationResult.isValid) return graph;

    const idMap = idMapFromRepoints(validationResult);

    const nodes = graph.nodes.filter((node) => !validationResult.nodesToRemove?.includes(node.id));

    nodes.forEach((node) => {
      if (model.nodes[node.id] || validationResult.duplicateIds?.includes(node.id)) {
        const newId = `${node.id}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        idMap.set(node.id, newId);
        node.id = newId;
      }
    });

    return { nodes, edges: remapEdges(graph.edges, idMap) };
  };

  const handleImportDxf = async (file: File, dxfUnitSystem: UnitSystem) => {
    if (!model) {
      showError('Model state not initialized');
      return;
    }
    try {
      setIsImporting(true);
      setError(null);

      const fileContent = await file.text();
      const graph = await processDxfToJson.processDxfToJson(fileContent, dxfUnitSystem);

      if (graph.nodes.length > 0) {
        const pipes = model.pipes as Record<string, PCPipe> | PCPipe[];
        const firstPipe = Array.isArray(pipes) ? pipes[0] : Object.values(pipes)[0];

        if (!firstPipe) {
          showError('No pipe data available. Please select pipes first.');
          setIsImporting(false);
          return;
        }

        const defaultPipeId = firstPipe.pipeId;
        graph.nodes.forEach((node) => {
          node.pipeId = defaultPipeId;
        });

        if (!isAdminOrPc) {
          const distanceResult = checkDistanceToCenterForPCNodes(model, graph.nodes);
          if (!distanceResult.isValid) {
            showError(distanceResult.errors.join(', '));
            setIsImporting(false);
            return;
          }
        }

        setIsPreviewOpen(true);
        previewDxfNodes(applyValidation(graph));
      } else {
        showError('No valid nodes found in the DXF file');
      }

      setIsImporting(false);
    } catch {
      showError('An error occurred while importing the DXF file');
      removePreviewNodes();
      setIsImporting(false);
    }
  };

  const handleSaveDxfNodes = async () => {
    try {
      setIsImporting(true);
      setError(null);
      setTimeout(() => setIsImporting(false), 100);

      if (!previewNodes || previewNodes.length === 0) {
        setError('No nodes to save');
        return;
      }

      const payload = buildImportPayload(previewNodes, previewEdges);

      mutation.mutate(
        {
          projectId: project.entityId,
          data: payload,
          projectName: project.name,
          nodeId: '',
        },
        {
          onSuccess: async () => {
            trackEvent('data_imported', {
              project_id: project.entityId,
              import_type: 'dxf',
              node_count: previewNodes.length,
            });
            setError(null);
            await mineModelQuery.refetch();
            setIsPreviewOpen(false);
            setPreviewNodes([]);
            setPreviewEdges([]);
            setPreviewSpools([]);
          },
          onError: () => {
            setError('Failed to save nodes. Please try again.');
            removePreviewNodes();
          },
        },
      );
    } catch {
      setError('Failed to save nodes');
    } finally {
      setIsImporting(false);
    }
  };

  const handleRemovePreview = () => {
    if (!model) {
      setError('Model state not initialized');
      return;
    }
    removePreviewNodes();
    setIsPreviewOpen(false);
  };

  return {
    isImporting,
    error,
    isPreviewOpen,
    previewNodes,
    handleImportDxf,
    handleSaveDxfNodes,
    handleRemovePreview,
  };
};

export default useDxfImport;
